import { isPlatformBrowser } from '@angular/common';
import { DestroyRef, Injectable, Injector, PLATFORM_ID, afterNextRender, effect, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { AnalyticsService, LocalizationService } from '@imagise/shared';
import { filter } from 'rxjs';
import { AnalyticsConsentChoice, AnalyticsConsentService } from './analytics-consent.service';
import { ANALYTICS_TRACKING_CONFIG } from './analytics-tracking.config';

@Injectable({ providedIn: 'root' })
export class AppAnalyticsService {
  readonly #analytics = inject(AnalyticsService);
  readonly #config = inject(ANALYTICS_TRACKING_CONFIG);
  readonly #consent = inject(AnalyticsConsentService);
  readonly #destroyRef = inject(DestroyRef);
  readonly #injector = inject(Injector);
  readonly #localization = inject(LocalizationService);
  readonly #platformId = inject(PLATFORM_ID);
  readonly #router = inject(Router);

  openSettings(): void {
    this.#consent.openSettings();
  }

  #initialized = false;
  #latestPageKey: string | null = null;
  #latestPagePath: string | null = null;
  #latestPageRevision = 0;
  #lastTrackedPageRevision = 0;
  #settledPageRevision = 0;
  #measurementId: string | null = null;
  #transportReady = false;
  #consentRevision = 0;

  init(measurementId: string | null): void {
    if (this.#initialized || !isPlatformBrowser(this.#platformId)) {
      return;
    }

    this.#initialized = true;
    this.#measurementId = measurementId?.trim() || null;
    if (this.#router.navigated) {
      const page = this.#config.resolvePage(this.#router.url);
      if (page) {
        this.#latestPageKey = page.key;
        this.#latestPagePath = page.path;
        this.#latestPageRevision = 1;
        this.#settledPageRevision = 1;
      }
    }

    this.#router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe(event => {
        const page = this.#config.resolvePage(event.urlAfterRedirects);
        if (!page || page.key === this.#latestPageKey) {
          return;
        }

        this.#latestPageKey = page.key;
        this.#latestPagePath = page.path;
        this.#latestPageRevision += 1;
        const revision = this.#latestPageRevision;
        afterNextRender(
          () => {
            this.#settledPageRevision = Math.max(this.#settledPageRevision, revision);
            this.#trackLatestPage();
          },
          { injector: this.#injector }
        );
      });

    this.#consent.initialize();
    effect(
      () => {
        void this.#applyConsent(this.#consent.choice());
      },
      { injector: this.#injector }
    );
  }

  accept(): void {
    this.#consent.accept();
  }

  reject(): void {
    this.#stopTransport();
    this.#consent.reject();
  }

  async #applyConsent(choice: AnalyticsConsentChoice): Promise<void> {
    const revision = ++this.#consentRevision;
    this.#transportReady = false;

    if (choice !== 'accepted' || !this.#measurementId) {
      this.#analytics.disable();
      return;
    }

    await this.#analytics.enable(this.#measurementId, this.#config.buildAllowlist(this.#localization));
    if (revision !== this.#consentRevision || this.#consent.choice() !== 'accepted') {
      return;
    }

    this.#transportReady = true;
    this.#trackLatestPage();
  }

  #stopTransport(): void {
    this.#consentRevision += 1;
    this.#transportReady = false;
    this.#analytics.disable();
  }

  #trackLatestPage(): void {
    const path = this.#latestPagePath;
    if (
      !this.#transportReady ||
      !path ||
      this.#latestPageRevision !== this.#settledPageRevision ||
      this.#latestPageRevision === this.#lastTrackedPageRevision
    ) {
      return;
    }

    const page = { key: this.#latestPageKey ?? path, path };
    const contentLanguage = this.#config.contentLanguage(page, this.#localization);
    this.#analytics.track('page_view', {
      page_path: path,
      content_language: contentLanguage,
    });
    this.#config.trackPageExtras?.(this.#analytics, page, this.#localization);
    this.#lastTrackedPageRevision = this.#latestPageRevision;
  }
}
